import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePrefs } from '../contexts/PrefsContext';
import { usePlay } from '../contexts/PlayContext';
import { setUsername as saveUsername, createShare } from '../services/shareService';
import { trackShare } from '../services/analyticsService';
import { renderShareCard, scoredGamesForCard } from '../utils/shareCard';
import ShareTargets from './ShareTargets';

/**
 * The sharer's half of the flow, rendered **inside the Scores block** behind
 * its "Invite" link (revised 09-20). It was briefly its own section on Home;
 * before that, a `/share` route with a composer and a confirmation step.
 *
 * Two states:
 *   no username -> a field and a button, and nothing else. A card reading
 *                  "- scored 3/6" is broken, so sharing is gated on a name.
 *   has one     -> a Copy button and the share targets, in one row.
 *
 * **The card is still generated and uploaded, it just isn't shown** - and
 * neither is the URL, which people found confusing. The row is Copy plus the
 * share targets; every one of them carries the same lander link, and the
 * platforms unfurl the card from its `og:image`. The URL only appears as a
 * fallback, when the clipboard refuses.
 *
 * **It is built and uploaded when this panel opens, not when Copy is pressed**
 * (D52). That leaves `share.url` sitting as a plain string in the click
 * handler, so the clipboard write runs synchronously - iOS Safari silently
 * drops a write that has lost its user-gesture context. Don't move the upload
 * into `onCopy`.
 *
 * There's no Done button: the "Share scores" button that opened this is still
 * on screen and toggles it shut.
 */
function SharePanel() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { favoriteOrder } = usePrefs();
  const { friendsData, loadFriendsData } = usePlay();

  const [nameDraft, setNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [share, setShare] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Set only when the clipboard write fails, so there's still a way to get the
  // link out. Not shown otherwise - the bare URL was the confusing part.
  const [showLinkFallback, setShowLinkFallback] = useState(false);

  // AuthContext first, because that's the copy `refreshUser()` updates - which
  // is how a name set right here reaches the card without a reload. The
  // dashboard's copy is the fallback for a session restored before this
  // component mounted.
  const username = user?.username || friendsData?.user?.username || null;

  const buildShare = useCallback(async () => {
    if (!username) return;

    setIsBuilding(true);
    setError(null);
    try {
      const entries = scoredGamesForCard(friendsData?.user?.today_plays, favoriteOrder);
      const image = await renderShareCard({
        username,
        entries,
        dateLabel: new Date().toLocaleDateString(undefined, {
          weekday: 'long', month: 'long', day: 'numeric',
        }),
      });

      const created = await createShare({
        image,
        scored: entries.map((entry) => ({ gameId: entry.game.id, score: entry.score })),
      });
      setShare(created);
    } catch (err) {
      console.log('Error preparing share:', err);
      setError("Couldn't prepare your share link. Please try again.");
    } finally {
      setIsBuilding(false);
    }
  }, [username, friendsData, favoriteOrder]);

  // Once on open, and again if the name arrives after it.
  useEffect(() => {
    if (username && !share && !isBuilding) {
      buildShare();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const onSubmitName = async (e) => {
    e.preventDefault();
    const trimmed = nameDraft.trim();
    if (!trimmed) return;

    setIsSavingName(true);
    setError(null);
    try {
      await saveUsername(trimmed);
      // The name has to reach the rest of the app without a reload: the card
      // is drawn from it, and so is the scores table - which reads its own
      // copy from the dashboard, hence both refreshes rather than one.
      await refreshUser();
      loadFriendsData(true).catch(() => {
        // The name is saved either way; the table catches up on next load.
      });
      setShare(null);
    } catch (err) {
      console.log('Error setting username:', err);
      setError("Couldn't save that name. Please try again.");
    } finally {
      setIsSavingName(false);
    }
  };

  // The write is *started* synchronously inside the handler, deliberately - see
  // the note above. Only the outcome is handled asynchronously: writeText
  // returns a promise, and a rejection (permission denied, insecure context)
  // is not caught by a try/catch around the call.
  const onCopy = () => {
    if (!share) return;
    trackShare('copy');
    const message = `Come play a daily round of puzzles with me: ${share.url}`;

    const onCopied = () => {
      setShowLinkFallback(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    const onFailed = (err) => {
      console.log('Error copying share link:', err);
      setShowLinkFallback(true);
    };

    try {
      Promise.resolve(navigator.clipboard.writeText(message)).then(onCopied, onFailed);
    } catch (err) {
      // No clipboard API at all (older browser, insecure context).
      onFailed(err);
    }
  };

  return (
    <div className="px-3 py-3 space-y-3">
      {error && (
        <p className="px-3 py-2 rounded-lg text-sm text-nlg-cream bg-red-900/40" role="alert">
          {error}
        </p>
      )}

      {!username ? (
        <form onSubmit={onSubmitName} className="space-y-2">
          <label htmlFor="share-username" className="block text-sm text-nlg-muted">
            Pick a display name first — it's what your friends will see next to your scores.
          </label>
          <div className="flex items-center gap-2">
            <input
              id="share-username"
              type="text"
              maxLength={10}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="Your name"
              className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-nlg-text placeholder:text-nlg-muted focus:outline-none focus:border-nlg-gold/50"
            />
            <button
              type="submit"
              disabled={isSavingName || !nameDraft.trim()}
              className="px-4 py-2 rounded-lg bg-nlg-gold text-nlg-bg text-sm font-bold disabled:opacity-50"
            >
              {isSavingName ? 'Saving…' : 'Set name'}
            </button>
          </div>
          <p className="text-sm text-nlg-muted">Up to 10 characters. You can change it later.</p>
        </form>
      ) : (
        <>
          <p className="text-sm text-nlg-muted">
            Invite a friend. They'll see today's scores, and you'll see each
            other's from then on.
          </p>

          {/* One row: Copy first, then the targets. They render disabled until
              the link exists rather than appearing late. */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={onCopy}
              disabled={!share}
              className="flex-shrink-0 inline-flex items-center justify-center h-11 min-w-[5.5rem] px-4 rounded-full bg-nlg-gold text-nlg-bg text-sm font-bold disabled:opacity-50"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <ShareTargets url={share?.url ?? null} username={username} />
          </div>

          {isBuilding && !share && (
            <p className="text-sm text-nlg-muted">Preparing your link…</p>
          )}

          {/* The row is disabled until a link exists, so a failed build needs
              its own way out. */}
          {error && !share && !isBuilding && (
            <button onClick={buildShare} className="text-sm font-bold text-nlg-gold">
              Try again
            </button>
          )}

          {showLinkFallback && share && (
            <div className="space-y-1">
              <p className="text-sm text-nlg-muted">Couldn't copy automatically. Here's your link:</p>
              <input
                readOnly
                value={share.url}
                onFocus={(e) => e.target.select()}
                aria-label="Your share link"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-nlg-rule text-sm text-nlg-text"
              />
            </div>
          )}

          {/* A better-earned ask than the ladder's sign-up rung: they've
              just put their name on a card and invited people, and if they
              lose the token the friends they're about to gain point at an
              account they can't reach (D11/D35). */}
          {!isAuthenticated && (
            <p className="text-sm text-nlg-muted">
              <Link to="/signup" className="font-bold text-nlg-gold">Sign up</Link> so the
              friends you're inviting stay connected to you.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default SharePanel;
